/**
 * Centralised ID generation for CheerEval.
 *
 * Rules (see AGENTS.md for full convention):
 *  - NEVER use `crypto.randomUUID()` — unavailable in jsdom/Jest.
 *  - Firestore document IDs: use `addDoc()` (let Firestore generate the ID).
 *  - Embedded/structural IDs (fields, groups, templates): use the typed
 *    helpers below, which produce a namespaced UUID v4.
 */

/** UUID v4 via `crypto.getRandomValues` — safe in browsers, Node, and jsdom. */
function generateUUID(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** ID for a `FormField` embedded in a form schema. */
export function generateFieldId(): string {
  return `f_${generateUUID()}`;
}

/** ID for a `FormGroup` embedded in a form schema. */
export function generateGroupId(): string {
  return `g_${generateUUID()}`;
}

/**
 * Client-side ID for a `FormTemplate`.
 * Prefer letting Firestore auto-generate document IDs via `addDoc()` where
 * possible; use this only when a stable id is needed before the first save.
 */
export function generateTemplateId(): string {
  return `tpl_${generateUUID()}`;
}

/** ID for a `GeneralCommentEntry` embedded in an athlete's scores. */
export function generateCommentId(): string {
  return `gc_${generateUUID()}`;
}

/** Raw UUID for use as a Firebase Storage filename (no entity prefix). */
export function generateStorageKey(): string {
  return generateUUID();
}
